"use client";

import React, { useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import classNames from "classnames";
import Link from "next/link";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/autoplay";

import { Search } from "./Search";

interface HeroSlide {
  id: number;
  title: string;
  description: string;
  coverImage: string;
  buttonText?: string;
  buttonUrl?: string;
  buttonStyle?: string;
  buttonTarget?: string;
  showSearch?: boolean;
  showButton?: boolean;
  templateType?: string;
  contentBackgroundColor?: string;
  contentBackgroundOpacity?: number;
  hideOverlay?: boolean;
  titleColor?: string;
  descriptionColor?: string;
}

interface HeroSliderProps {
  slides: HeroSlide[];
  sliderSettings?: {
    autoplay?: boolean;
    navigation?: boolean;
    pagination?: boolean;
    delay?: number;
    speed?: number;
    loop?: boolean;
  };
  backgroundColor?: string;
  textColor?: string;
  extraClass?: string;
  blockStyle?: any;
  themeConfig?: any;
}

const HeroSlideComponent: React.FC<{
  slide: HeroSlide;
  backgroundColor?: string;
  textColor?: string;
  slideIndex: number;
  themeConfig?: any;
}> = ({ slide, backgroundColor, textColor, slideIndex, themeConfig }) => {

  // Function to get content background styles
  const getContentBackgroundStyle = () => {
    if (slide.hideOverlay) {
      return {
        backgroundColor: 'transparent',
        background: 'none',
        border: 'none',
        boxShadow: 'none'
      };
    }

    // Always white with 80% opacity
    return {
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      background: 'rgba(255, 255, 255, 0.8)',
      border: '1px solid rgba(229, 231, 235, 0.5)',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
    };
  };

  const elementStyle = slide.coverImage ? {
    backgroundImage: `url(${slide.coverImage})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  } : {};

  return (
    <div
      className="hero-slide w-full h-[400px] relative"
      style={elementStyle}
    >
      <div className="container h-full flex justify-center items-start relative z-10">
        <div className="w-full flex justify-center items-start">
          <div
            className="flex flex-col items-center p-6 md:p-8 rounded-lg min-w-[339px] md:min-w-[400px] mx-auto mt-[36px]"
            style={getContentBackgroundStyle()}
            suppressHydrationWarning
          >
            <div className="flex flex-col gap-1 md:gap-2 text-center w-full">
              {/* Title - RichText HTML from WordPress */}
              {slide.title && (
                <div
                  className="hero-title font-bold text-6xl md:text-7xl leading-[110%]"
                  style={{ color: slide.titleColor || '#21586c' }}
                  dangerouslySetInnerHTML={{ __html: slide.title }}
                />
              )}

              {/* Description - RichText HTML from WordPress */}
              {slide.description && (
                <div
                  className="hero-description font-normal text-xl md:text-6xl leading-[150%] md:leading-[100%]"
                  style={{ color: slide.descriptionColor || '#21586c', fontFamily: 'Open Sans, sans-serif' }}
                  dangerouslySetInnerHTML={{ __html: slide.description }}
                />
              )}

              {/* Search Field */}
              {slide.showSearch && (
                <div className="w-full flex justify-center">
                  <Search />
                </div>
              )}

              {/* Button */}
              {slide.showButton && slide.buttonText && (
                <div className="button-group w-full flex justify-center pt-6">
                  <Link
                    href={slide.buttonUrl || "#"}
                    target={slide.buttonTarget || '_self'}
                    rel={slide.buttonTarget === '_blank' ? 'noopener noreferrer' : undefined}
                    className="inline-flex items-end font-semibold text-center transition-all duration-300 hover:opacity-90"
                    style={{
                      padding: '9px 18px',
                      gap: '9px',
                      borderRadius: '8px',
                      background: slide.buttonStyle === 'dark' ? '#2D2D32' : '#FFF',
                      color: slide.buttonStyle === 'dark' ? '#ffffff' : '#0a5599',
                      textDecoration: 'none',
                      border: slide.buttonStyle === 'light' ? '1px solid #e5e7eb' : 'none'
                    }}
                  >
                    {slide.buttonText}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const HeroSlider: React.FC<HeroSliderProps> = ({
  slides,
  sliderSettings,
  backgroundColor,
  textColor,
  extraClass,
  blockStyle,
  themeConfig,
}) => {
  const [swiperIsLoaded, setSwiperIsLoaded] = useState(false);
  const sliderRef = useRef<any>(null);

  // Provide default values for sliderSettings
  const settings = {
    autoplay: true,
    navigation: false,
    pagination: true,
    delay: 10000,
    speed: 1000,
    loop: false,
    ...sliderSettings
  };

    const slidesFiltered = slides.filter((heroBlock: any) => {
    // Filter out slides with isVisible === false (strict check)
    if (heroBlock.isVisible === false) {
      return false;
    }

    // Check date-based visibility
    const now = new Date();

    // If startDate is set and is in the future, filter out
    if (heroBlock.startDate) {
      const startDate = new Date(heroBlock.startDate);
      if (now < startDate) {
        return false;
      }
    }

    // If endDate is set and has passed, filter out
    if (heroBlock.endDate) {
      const endDate = new Date(heroBlock.endDate);
      if (now > endDate) {
        return false;
      }
    }

    return true;
  });

  const containerClass = classNames(
    "full-width h-[400px] md:mb-0 hero-slider-container",
    { [`bg-${backgroundColor || "gray-light"}`]: true },
    { [`text-${textColor || "black"}`]: true },
    { [extraClass || ""]: extraClass },
    { "visibility-visible": swiperIsLoaded }
  );

  const swiperLoaded = () => {
    setSwiperIsLoaded(true);
  };

  return (
    <>
      <style>
        {`
          .hero-slider-container .swiper-button-next,
          .hero-slider-container .swiper-button-prev {
            color: #fff;
            background: rgba(0, 0, 0, 0.5);
            border-radius: 50%;
            width: 44px;
            height: 44px;
            margin-top: -22px;
          }

          .hero-slider-container .swiper-button-next:after,
          .hero-slider-container .swiper-button-prev:after {
            font-size: 18px;
          }

          .hero-slider-container .swiper-pagination-bullet {
            width: 12px;
            height: 12px;
            background: transparent;
            border: 2px solid rgba(255, 255, 255, 1);
            opacity: 1;
          }

          .hero-slider-container .swiper-pagination-bullet-active {
            background: rgba(255, 255, 255, 1);
            border: 2px solid rgba(255, 255, 255, 1);
          }

          .hero-slider-container .swiper-pagination {
            bottom: 20px;
          }
        `}
      </style>
      <div
        className={containerClass}
        style={{...blockStyle, position: 'relative'}}
        suppressHydrationWarning
      >
        {/* Gradient overlay at bottom - 60px high - only show when pagination is enabled */}
        {settings.pagination && (
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '60px',
              background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.25) 100%)',
              pointerEvents: 'none',
              zIndex: 10
            }}
          />
        )}
        <Swiper
          slidesPerView={1}
          slidesPerGroup={1}
          spaceBetween={0}
          centeredSlides={false}
          watchOverflow={true}
          allowTouchMove={true}
          simulateTouch={true}
          preventClicks={false}
          preventClicksPropagation={false}
          autoplay={
            settings.autoplay
              ? {
                  delay: settings.delay,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }
              : false
          }
          navigation={settings.navigation}
          pagination={
            settings.pagination
              ? {
                  clickable: true,
                  dynamicBullets: false,
                }
              : false
          }
          loop={settings.loop}
          speed={settings.speed}
          onSwiper={swiperLoaded}
          ref={sliderRef}
          className="w-full h-full"
          modules={[Autoplay, Navigation, Pagination]}
        >
          {slidesFiltered.map((slide, index) => (
            <SwiperSlide key={slide.id} className="w-full h-full">
              <HeroSlideComponent
                slide={slide}
                backgroundColor={backgroundColor}
                textColor={textColor}
                slideIndex={index}
                themeConfig={themeConfig}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </>
  );
};
